/**
 * Pre-acquisition listing status for a `pipeline_properties` row.
 *
 * This is the MARKET status of a property we don't own yet, sourced from the
 * property API and refreshed while the deal sits in the pipeline: is it still
 * available, under contract with someone else, or already sold out from under
 * us? It has nothing to do with `is_closed`, which is our own disposition
 * *after* we own the property.
 *
 * `pipeline_properties.listing_status` is the source of truth. The legacy
 * `property_data.status` JSONB field is consulted as a fallback so rows read
 * from a cache written before the column landed still render correctly.
 *
 * The vocabulary mirrors the column's CHECK constraint and
 * `supabase/functions/_shared/listing-status.ts`. All three must stay in step.
 */
export type ListingStatus =
  | "active"
  | "pending"
  | "contingent"
  | "price_reduced"
  | "sold"
  | "off_market";

const LISTING_STATUSES: readonly ListingStatus[] = [
  "active",
  "pending",
  "contingent",
  "price_reduced",
  "sold",
  "off_market",
] as const;

/** Human labels for the kanban pill and any status filter. */
export const LISTING_STATUS_LABELS: Record<ListingStatus, string> = {
  active: "Active",
  pending: "Pending",
  contingent: "Contingent",
  price_reduced: "Price Reduced",
  sold: "Sold",
  off_market: "Off Market",
};

/**
 * Pill classes per status — semantic tokens only, so dark mode comes for free.
 * Green = go, amber = someone else is circling, grey/red = you can't buy it.
 */
export const LISTING_STATUS_PILL: Record<ListingStatus, string> = {
  active: "bg-success text-success-foreground",
  pending: "bg-warning text-warning-foreground",
  contingent: "bg-warning text-warning-foreground",
  price_reduced: "bg-primary text-primary-foreground",
  sold: "bg-muted-foreground text-background",
  off_market: "bg-muted text-muted-foreground",
};

/** Structural subset of a `pipeline_properties` row that these helpers read. */
export interface ListingStatusSource {
  listing_status?: string | null;
  /** The JSONB blob — `unknown` because callers hold it at several types. */
  property_data?: unknown;
}

/**
 * Coerce an arbitrary string to a known status. Anything unrecognized becomes
 * "active" — the non-blocking default, since a wrong "sold" would refuse a
 * valid acquisition.
 */
export function normalizeListingStatus(
  value: string | null | undefined,
): ListingStatus {
  const key = (value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return LISTING_STATUSES.find((s) => s === key) ?? "active";
}

/** `property_data.status` when the blob is an object, else undefined. */
function legacyBlobStatus(propertyData: unknown): string | undefined {
  if (!propertyData || typeof propertyData !== "object") return undefined;
  const raw = (propertyData as Record<string, unknown>).status;
  return typeof raw === "string" ? raw : undefined;
}

/**
 * Resolve a row's listing status, preferring the column and falling back to the
 * legacy JSONB flag only when the column is absent (stale cache / pre-migration
 * row). An empty-but-present column value still wins as "active".
 */
export function resolveListingStatus(row: ListingStatusSource): ListingStatus {
  if (row.listing_status != null) return normalizeListingStatus(row.listing_status);
  return normalizeListingStatus(legacyBlobStatus(row.property_data));
}

/**
 * True when the property has already sold to someone else — the one status that
 * must block marking the deal owned. "pending"/"contingent" deliberately do not
 * block: deals fall out of contract all the time and the user may well be the
 * backup offer.
 */
export function blocksAcquisition(status: ListingStatus): boolean {
  return status === "sold";
}

/** Convenience: does this row's listing status block acquisition? */
export function isSoldListing(row: ListingStatusSource): boolean {
  return blocksAcquisition(resolveListingStatus(row));
}

/** Outcome of the Closed-Won gate. `warn` carries a non-blocking surprise. */
export type AcquisitionGate =
  | { allow: true; warn?: ListingStatus }
  | { allow: false; reason: ListingStatus };

/**
 * Decide whether a deal may be marked owned, given the status we had stored and
 * the one the feed just returned.
 *
 * `fresh` is undefined when the feed couldn't be read. That falls back to
 * `stored` rather than blocking: an API hiccup must not stop a user from
 * recording a real acquisition, and the DB gate in `moveToClosedWonMutation` is
 * the actual backstop.
 *
 * A non-active status that differs from what we had stored comes back as a
 * `warn` — the move proceeds, but the user learns the listing moved under them
 * before they record a purchase.
 */
export function decideAcquisitionGate(
  stored: ListingStatus,
  fresh: ListingStatus | undefined,
): AcquisitionGate {
  const effective = fresh ?? stored;
  if (blocksAcquisition(effective)) return { allow: false, reason: effective };
  if (effective !== "active" && effective !== stored) {
    return { allow: true, warn: effective };
  }
  return { allow: true };
}
